/**
 * MapCanvas Component
 * The WebGL surface. One MapLibre instance, created lazily and destroyed on
 * unmount, with the lifecycle written around two facts about the shell it
 * lives in:
 *
 *  1. `Cmarkdownvisual` portals its children UNCONDITIONALLY, including in
 *     compact mode, where the portal host has no target and stays detached from
 *     the document. A canvas that instantiated on mount would allocate a WebGL
 *     context per collapsed block against a zero-sized, detached node — and
 *     browsers cap contexts at roughly 16. So instantiation waits until the
 *     container is genuinely connected and laid out, and `MapCompactPreview`
 *     never renders this component at all.
 *  2. The portal host is REPARENTED, not recreated, across canvas <-> modal
 *     transitions. One instance survives that move — which is why the map is
 *     not keyed to the surface — but MapLibre's own `trackResize` watches the
 *     *window* and cannot see it. The ResizeObserver below is what makes the
 *     transition correct.
 *
 * MapLibre and its stylesheet are imported dynamically INSIDE the effect: the
 * library touches `window` at module scope and must never be evaluated during
 * SSR. There is deliberately no top-level `maplibre` import in this file, not
 * even a type-only one — `type ... = typeof import(...)` carries the types
 * without emitting an import.
 */

'use client';

import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { useReducedMotion } from 'framer-motion';
import { ButtonPreset } from '@/constants/componentPresets';
import { Cbutton } from '../Cbutton';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  AMBIENT_POI_MIN_ZOOM,
  applyMapAmbientPoi,
  ambientPoiText,
  MAP_AMBIENT_POI_LAYER_ID,
} from './map-ambient-poi';
import { applyMapCartography } from './map-cartography';
import { mapPinIconForKeyword } from './map-pin-icon';
import {
  useMapAmbientPoiVariant,
  useMapPoiFollowUp,
} from './map-presentation.context';
import {
  applyMapPinReservation,
  followUpFloorsZoom,
  followUpReservesPins,
} from './map-poi-follow-up';
import {
  MAP_COORDINATE_FRACTION_DIGITS,
  MAP_DEFAULT_ZOOM,
  MAP_ENTER_SETTLE_MS,
  MAP_FIT_MAX_ZOOM,
  MAP_FIT_PADDING,
  MAP_GLOBE_PROJECTION,
  MAP_INTRO_FLY_DURATION_MS,
  MAP_INTRO_HOLD_MS,
  MAP_INTRO_START_ZOOM,
  MAP_LOAD_TIMEOUT_MS,
} from './map-constants';
import { isKeyboardSynthesizedClick } from './map-marker-interaction';
import type { MapCanvasPoint } from './map-render-model';
import { ensureMapWorkerUrl } from './map-worker';
import {
  boundsForPoints,
  mapStyleForTheme,
  mapStyleUrl,
  type MapPoint,
} from './map-style';

type MapLibreModule = typeof import('maplibre-gl');
type MapLibreMapInstance = InstanceType<MapLibreModule['Map']>;
type MapLibreMarkerInstance = InstanceType<MapLibreModule['Marker']>;
type MapLibrePopupInstance = InstanceType<MapLibreModule['Popup']>;
/** The event a LAYER-scoped listener receives — the same mouse event plus the
 *  features under the pointer, which is how an ambient POI click knows which
 *  place it hit. */
type MapLibreLayerMouseEvent = import('maplibre-gl').MapLayerMouseEvent;
type MapLibreMapMouseEvent = import('maplibre-gl').MapMouseEvent;

/** MapLibre event names this component subscribes to. */
const MAP_LOAD_EVENT = 'load';
const MAP_STYLE_LOAD_EVENT = 'style.load';
/** Fired when the map has finished settling after any change. The reservation
 *  hangs off it because a variant flip lands while the style still reports
 *  itself mid-write, and `style.load` only fires for a NEW style — so without
 *  this a flip on a settled map would apply nothing and never retry. */
const MAP_IDLE_EVENT = 'idle';
const MAP_ERROR_EVENT = 'error';
const MAP_CLICK_EVENT = 'click';
const MAP_MOUSE_ENTER_EVENT = 'mouseenter';
const MAP_MOUSE_LEAVE_EVENT = 'mouseleave';

/** The canvas cursor while an ambient POI is under the pointer. Empty is
 *  MapLibre's own default, which its drag handlers manage. */
const CURSOR_POINTER = 'pointer';
const CURSOR_DEFAULT = '';

/** Bidi direction for marker and popup DOM: `markdown-hebrew.ts` covers
 *  markdown text elements only and never reaches this DOM. */
const DIR_AUTO = 'auto';

/** DOM attributes this component writes onto MapLibre-owned elements. */
const TABINDEX_ATTRIBUTE = 'tabindex';
const ARIA_LABEL_ATTRIBUTE = 'aria-label';

// Chromeless on purpose: the map owns the whole expanded stage and the card
// rail floats over it, so any radius or inset ring here would draw a phantom
// frame behind the floating cards.
const MAP_CANVAS_ROOT_STYLE: CSSProperties = {
  position: 'relative',
  flex: 1,
  width: '100%',
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: 'rgba(0,0,0,0.35)',
};

/** Class hooks for the POI marker DOM. The looks — the muted idle glyph pin
 *  and the chosen pin's beam, ring and glow — live in MapExpandedSurface.css,
 *  keyed on the stage's pin-style attributes; the marker carries structure
 *  only. A pin never draws its own name: the name lives in the aria-label,
 *  the click popup and the rail card below. */
const MARKER_ROOT_CLASS = 'map-poi';
/** Applied to the chosen marker — picked from its rail card or clicked
 *  directly. The baked Halo treatment (vibrant body under an expanding ring)
 *  hangs off it. */
const MARKER_CHOSEN_CLASS = 'map-poi--chosen';
const MARKER_DOT_CLASS = 'map-poi__dot';
const MARKER_ICON_CLASS = 'map-poi__icon';
/** Structured click popup (Atlas stage family): index + name + coordinates. */
const POPUP_ROOT_CLASS = 'map-poi-popup';
const POPUP_TITLE_ROW_CLASS = 'map-poi-popup__title-row';
const POPUP_INDEX_CLASS = 'map-poi-popup__index';
const POPUP_NAME_CLASS = 'map-poi-popup__name';
const POPUP_DESCRIPTION_CLASS = 'map-poi-popup__description';
const POPUP_COORDS_CLASS = 'map-poi-popup__coords';
/** The ambient-POI popup: the same glass card, marked so its quieter register
 *  (no index, a kind line instead of coordinates) can be styled apart from an
 *  authored pin's. */
const POPUP_AMBIENT_CLASS = 'map-poi-popup--ambient';
const POPUP_KIND_CLASS = 'map-poi-popup__kind';
/** MapLibre's own marker host. An ambient click that landed on an authored pin
 *  is the pin's, never the basemap's — see the click handler. */
const MAPLIBRE_MARKER_SELECTOR = '.maplibregl-marker';

// The popup ships ONLY on the baked dark-glass Atlas card, so its text is
// authored light. (It was #101418 for MapLibre's default white card, which
// left the name invisible — black on dark glass — once the restyle baked.)
//
// Every vertical metric here is a WHOLE pixel, and it has to stay that way:
// MapLibre positions the popup with `translate(-50%,-100%)` (or the -100%/0
// variants its edge anchors pick) applied to the popup container, which this
// box sizes — so a fractional line height lands the whole card, text
// included, on a half pixel. The width is pinned even-numbered in CSS for the
// same reason; the max-width that used to size this element by its own text
// is gone with it.
const POPUP_CONTENT_STYLE_TEXT = [
  `font-family: ${getFontFamily()}`,
  'font-size: 12px',
  'font-weight: 600',
  'line-height: 16px',
  'color: rgba(255, 255, 255, 0.94)',
].join('; ');

/** How many lines of an authored description a popup shows before clamping.
 *  A description is prose of unbounded length (up to the parser's per-line
 *  ceiling), and a popup that grows with it covers the map it points at. */
const POPUP_DESCRIPTION_LINE_CLAMP = 4;

/** Inherits the popup's colour at reduced weight and opacity, so it reads as
 *  supporting text under the name. The line-height is a whole pixel so the
 *  clamped block is always an exact multiple of it — see the box-height note
 *  on POPUP_CONTENT_STYLE_TEXT. */
const POPUP_DESCRIPTION_STYLE_TEXT = [
  'margin-top: 3px',
  'font-size: 11px',
  'font-weight: 400',
  'line-height: 15px',
  'opacity: 0.78',
  'white-space: normal',
  'overflow-wrap: anywhere',
  'display: -webkit-box',
  '-webkit-box-orient: vertical',
  `-webkit-line-clamp: ${POPUP_DESCRIPTION_LINE_CLAMP}`,
  'overflow: hidden',
].join('; ');

/** A rail-card click, addressed to one pin. The nonce distinguishes repeat
 *  clicks on the same card so every click re-flies. */
export interface MapCanvasFocus {
  key: string;
  lat: number;
  lon: number;
  nonce: number;
}

export interface MapCanvasProps {
  dataElScope: DataElScope;
  /** Positioned pins only. The parent filters unresolved places out. */
  points: MapCanvasPoint[];
  /** Embedder zoom override, or null to auto-fit. */
  zoom: number | null;
  /** Embedder centre override, or null to auto-fit across the points. */
  center: MapPoint | null;
  isDark: boolean;
  /** Announced name for the canvas, which has no intrinsic text. */
  accessibleName: string;
  /** The pin a rail-card click asked to focus, or null before any click. */
  focus?: MapCanvasFocus | null;
  /** The entry key of the chosen pin — the one carrying the chosen treatment
   *  and the open popup — or null while nothing is picked. */
  chosenKey?: string | null;
  /** Fired when a marker is activated on the map itself, so the host can
   *  choose the matching rail card. The selection state lives in the host —
   *  this only reports the pick. */
  onMarkerPick?: (key: string) => void;
  /** Fired on style failure, WebGL initialization failure, or context loss, so
   *  the parent can fall back to MapLocationList. */
  onUnavailable: () => void;
}

/** Focus flight tuning: never zoom OUT to focus, always land at least here. */
const MAP_FOCUS_MIN_ZOOM = 8;
const MAP_FOCUS_FLY_DURATION_MS = 950;

/** The live instance and the module that made it, kept together so the marker
 *  effect never has to reach for the module a second time. */
interface MapLibreLiveInstance {
  maplibre: MapLibreModule;
  map: MapLibreMapInstance;
}

interface CameraInput {
  map: MapLibreMapInstance;
  points: MapCanvasPoint[];
  zoom: number | null;
  center: MapPoint | null;
  /** The lowest zoom a FITTED frame may land on, or null for no floor. Set
   *  when the reader has asked that a block never open below the zoom the
   *  tiles carry places from. */
  floorZoom: number | null;
}

interface MarkersInput {
  maplibre: MapLibreModule;
  map: MapLibreMapInstance;
  points: MapCanvasPoint[];
  /** Reports a marker activation to the host. Read through a ref by the
   *  caller so a new callback identity never remounts the marker set. */
  onPick: (key: string) => void;
}

/** Clears the pin's rotated 30px body — its corners reach ~21px from the
 *  marker centre — so the popup never lands on the glyph it points at. */
const MARKER_POPUP_OFFSET = 24;

/** The ambient dot is far smaller than a pin, so its popup sits closer. */
const AMBIENT_POPUP_OFFSET = 10;

/** Attached, laid out, and part of the document — the three conditions that
 *  make a WebGL context worth allocating. */
function isContainerRenderable(container: HTMLDivElement): boolean {
  return (
    container.isConnected &&
    container.clientWidth > 0 &&
    container.clientHeight > 0
  );
}

interface MarkerElementInput {
  label: string;
  /** The authored icon keyword; the glyph is resolved here so the marker
   *  DOM carries a concrete icon even for the fallback. */
  icon: string | null;
  /** Reports the pick — pointer or keyboard — to the host. */
  onPick: () => void;
}

/** The glyph size inside the pin body — scaled with the 30px pin. */
const MARKER_ICON_SIZE = 16;
const MARKER_ICON_STROKE_WIDTH = 2.2;

/** A span-based `Cbutton` mounted into MapLibre's imperative marker host keeps
 *  markers reachable and keyboard-operable without bypassing the button system.
 *  `dir="auto"` because `markdown-hebrew.ts` covers
 *  markdown text elements only and never reaches marker DOM.
 *
 *  The button is a glyph pin: a body carrying the place-kind glyph, nothing
 *  textual. A pin never prints its own name — the name lives in the
 *  aria-label, the click popup, and the rail card the pick selects below —
 *  so the marker stays legible at thirty per canvas. MapLibre positions the
 *  root via inline transform, so all motion lives on the children, never the
 *  root.
 *
 *  MapLibre toggles the popup from its own `keypress` handler on the host.
 *  Cbutton's keyboard path invokes its callback without dispatching a DOM
 *  click, so that keypress remains the sole toggle. Any detail-0 click that a
 *  programmatic caller does synthesize is stopped here, while pointer clicks
 *  (detail >= 1) still travel to the map as before. The pick is reported on
 *  BOTH activation paths — it is selection state, not a popup concern. */
function createMarkerElement({
  label,
  icon,
  onPick,
}: MarkerElementInput): { element: HTMLDivElement; reactRoot: Root } {
  const element = document.createElement('div');
  const reactRoot = createRoot(element);
  const IconGlyph = mapPinIconForKeyword(icon);
  reactRoot.render(
    <Cbutton
      inline
      rootClassName={MARKER_ROOT_CLASS}
      rootAttributes={{ dir: DIR_AUTO }}
      preset={ButtonPreset.Ghost}
      ariaLabel={label}
      padding={0}
      onClick={(event) => {
        if (isKeyboardSynthesizedClick({ detail: event?.detail ?? 0 })) {
          event?.stopPropagation();
        }
        onPick();
      }}
      style={{ fontFamily: getFontFamily() }}
      containerStyle={{ display: 'contents' }}
    >
      <span className={MARKER_DOT_CLASS} aria-hidden="true">
        <span className={MARKER_ICON_CLASS}>
          <IconGlyph
            size={MARKER_ICON_SIZE}
            strokeWidth={MARKER_ICON_STROKE_WIDTH}
          />
        </span>
      </span>
    </Cbutton>,
  );
  return { element, reactRoot };
}

/** The click popup carries more than the name: the list index, the label, the
 *  author's description of the place when there is one, and the pin's
 *  coordinates (the "extra data on click" of the Atlas refinement — the dark
 *  glass restyle is scoped to that family in CSS).
 *
 *  Reading order is label → description → coordinates, and the description is
 *  clamped rather than allowed to grow: the popup floats over the map, so an
 *  essay would hide the geography it is describing. `dir="auto"` on its own
 *  element because a description may be in a different script from the label. */
function createPopupContent({
  label,
  description,
  index,
  lat,
  lon,
}: {
  label: string;
  description: string | null;
  index: number;
  lat: number;
  lon: number;
}): HTMLDivElement {
  const content = document.createElement('div');
  content.className = POPUP_ROOT_CLASS;
  content.dir = DIR_AUTO;
  content.style.cssText = POPUP_CONTENT_STYLE_TEXT;

  const titleRow = document.createElement('div');
  titleRow.className = POPUP_TITLE_ROW_CLASS;
  const indexBadge = document.createElement('span');
  indexBadge.className = POPUP_INDEX_CLASS;
  indexBadge.textContent = String(index);
  titleRow.appendChild(indexBadge);
  const name = document.createElement('span');
  name.className = POPUP_NAME_CLASS;
  name.dir = DIR_AUTO;
  name.textContent = label;
  titleRow.appendChild(name);
  content.appendChild(titleRow);

  if (description) {
    const descriptionElement = document.createElement('div');
    descriptionElement.className = POPUP_DESCRIPTION_CLASS;
    descriptionElement.dir = DIR_AUTO;
    descriptionElement.style.cssText = POPUP_DESCRIPTION_STYLE_TEXT;
    descriptionElement.textContent = description;
    content.appendChild(descriptionElement);
  }

  const coords = document.createElement('div');
  coords.className = POPUP_COORDS_CLASS;
  coords.textContent = `${lat.toFixed(MAP_COORDINATE_FRACTION_DIGITS)}, ${lon.toFixed(MAP_COORDINATE_FRACTION_DIGITS)}`;
  content.appendChild(coords);

  return content;
}

/**
 * The ambient POI's popup — the same glass card in a quieter register.
 *
 * It carries what the tiles actually know and nothing more: the place's name
 * and its kind. No index, because an ambient place is not in the block's
 * numbered list; no coordinates, because the reader did not author this place
 * and a lat/lon they cannot act on is noise. A class with no kind label prints
 * the name alone rather than a raw OSM token.
 */
function createAmbientPopupContent({
  name,
  kind,
}: {
  name: string;
  kind: string | null;
}): HTMLDivElement {
  const content = document.createElement('div');
  content.className = `${POPUP_ROOT_CLASS} ${POPUP_AMBIENT_CLASS}`;
  content.dir = DIR_AUTO;
  content.style.cssText = POPUP_CONTENT_STYLE_TEXT;

  const titleRow = document.createElement('div');
  titleRow.className = POPUP_TITLE_ROW_CLASS;
  const nameElement = document.createElement('span');
  nameElement.className = POPUP_NAME_CLASS;
  nameElement.dir = DIR_AUTO;
  nameElement.textContent = name;
  titleRow.appendChild(nameElement);
  content.appendChild(titleRow);

  if (kind) {
    const kindElement = document.createElement('div');
    kindElement.className = POPUP_KIND_CLASS;
    kindElement.textContent = kind;
    content.appendChild(kindElement);
  }

  return content;
}

interface MountedMarker {
  marker: MapLibreMarkerInstance;
  reactRoot: Root;
}

/**
 * Whether a Map object is one that can actually be drawn on.
 *
 * MapLibre's `Map` constructor does NOT throw when the GPU refuses it. When
 * `getContext('webgl2')` answers null, `_setupPainter` fires an `error` event
 * carrying `GPUInitializationError` and RETURNS, and the constructor's own
 * `if (!this.painter) return` hands back a half-built map: no painter, no
 * handler manager, no resize observer, no style, and a camera transform that
 * was never sized. The object is a `Map` by every type the library exports and
 * by every check `instanceof` can make — `painter` is the one thing that says
 * the construction stopped early.
 *
 * That gap is what took the whole page down on 2026-08-23. Three frames, all
 * from one unguarded object:
 *   1. `_setupPainter` logs `GPUInitializationError` and returns — no throw, so
 *      `startMap`'s own `catch` never runs and the instance was published;
 *   2. `addMarkers` → `Marker.addTo` → `Map.project` → `transform.
 *      coordinatePoint` reads `[0]` of a matrix the early return never built:
 *      `TypeError: Cannot read properties of undefined (reading '0')`, thrown
 *      inside a passive effect;
 *   3. the effect cleanup React then runs calls `Map.remove()`, which
 *      dereferences `this.painter.destroy()` unconditionally:
 *      `TypeError: Cannot read properties of undefined (reading 'destroy')`.
 *
 * Read through `unknown` because the library's own types declare `painter` as
 * always present, which is exactly the claim the early return breaks.
 */
function isMapDrawable(map: MapLibreMapInstance): boolean {
  const painter: unknown = map.painter;
  return painter != null;
}

function addMarkers({
  maplibre,
  map,
  points,
  onPick,
}: MarkersInput): MountedMarker[] {
  // The 1-based popup index matches MapLocationList's numbering, so a popup
  // and its list row read as the same place.
  return points.map((point, pointIndex) => {
    const markerElement = createMarkerElement({
      label: point.label,
      icon: point.icon,
      onPick: () => {
        onPick(point.key);
      },
    });
    const popupContent = createPopupContent({
      label: point.label,
      description: point.description,
      index: pointIndex + 1,
      lat: point.lat,
      lon: point.lon,
    });
    const popup = new maplibre.Popup({
      closeButton: false,
      offset: MARKER_POPUP_OFFSET,
    }).setDOMContent(popupContent);
    const marker = new maplibre.Marker({
      element: markerElement.element,
    })
      .setLngLat([point.lon, point.lat])
      .setPopup(popup)
      .addTo(map);
    // The focus target is the nested Cbutton. Keep MapLibre's host out of the
    // tab order while allowing its keypress listener to receive bubbled events.
    markerElement.element.removeAttribute(TABINDEX_ATTRIBUTE);
    return { marker, reactRoot: markerElement.reactRoot };
  });
}

/** Where the intro's planet-altitude view waits before the descent: over the
 *  same geography it will land on, so the flight is a straight drop rather
 *  than a swing around the sphere. */
function introStartCenter({
  points,
  center,
}: Pick<CameraInput, 'points' | 'center'>): [number, number] | null {
  if (center) {
    return [center.lon, center.lat];
  }
  const bounds = boundsForPoints(points);
  if (!bounds) {
    return null;
  }
  return [
    (bounds.minLon + bounds.maxLon) / 2,
    (bounds.minLat + bounds.maxLat) / 2,
  ];
}

/** The intro descent. Same camera precedence as `applyCamera` — an embedder
 *  centre outright, fitted bounds otherwise, an embedder zoom on top — so
 *  the flight lands on exactly the frame the snap would have shown. */
function flyIntroCamera({
  map,
  points,
  zoom,
  center,
  floorZoom,
}: CameraInput): void {
  if (center) {
    map.flyTo({
      center: [center.lon, center.lat],
      zoom: zoom ?? MAP_DEFAULT_ZOOM,
      duration: MAP_INTRO_FLY_DURATION_MS,
      essential: true,
    });
    return;
  }

  const bounds = boundsForPoints(points);
  if (!bounds) {
    return;
  }
  const cornerBounds: CameraCorners = [
    [bounds.minLon, bounds.minLat],
    [bounds.maxLon, bounds.maxLat],
  ];

  const boundsMidpoint: [number, number] = [
    (bounds.minLon + bounds.maxLon) / 2,
    (bounds.minLat + bounds.maxLat) / 2,
  ];
  // A fit that cannot be computed must still land. `fitBounds` answers a
  // canvas it cannot solve for — one that has not been laid out yet, or a
  // degenerate single-pin box under the intro's globe projection — by logging
  // `Map cannot fit within canvas` and doing NOTHING, which leaves the camera
  // parked on the world at the intro's own zoom. The descent then never
  // happens and the reader is looking at a planet where a street was promised.
  const camera = solveCameraForBounds(map, cornerBounds);

  map.flyTo({
    center: camera?.center ?? boundsMidpoint,
    zoom: resolveFrameZoom({ zoom, solvedZoom: camera?.zoom, floorZoom }),
    duration: MAP_INTRO_FLY_DURATION_MS,
    essential: true,
  });
}

/**
 * The zoom a frame lands on, floor applied.
 *
 * An embedder zoom is never floored. A caller that named its own zoom said
 * what it wanted, and a floor that overrode it would be a presentation control
 * editing the embed rather than dressing it.
 */
function resolveFrameZoom({
  zoom,
  solvedZoom,
  floorZoom,
}: {
  readonly zoom: number | null;
  readonly solvedZoom: number | undefined;
  readonly floorZoom: number | null;
}): number {
  if (zoom != null) {
    return zoom;
  }
  const fitted = solvedZoom ?? MAP_DEFAULT_ZOOM;
  return floorZoom == null ? fitted : Math.max(fitted, floorZoom);
}

/** An embedder centre wins outright; otherwise the points are fitted, with
 *  a zoom ceiling so a single pin does not land on a bare street corner. An
 *  embedder zoom is applied on top of the fitted centre. */
function applyCamera({
  map,
  points,
  zoom,
  center,
  floorZoom,
}: CameraInput): void {
  if (center) {
    map.jumpTo({ center: [center.lon, center.lat], zoom: zoom ?? MAP_DEFAULT_ZOOM });
    return;
  }

  const bounds = boundsForPoints(points);
  if (!bounds) {
    return;
  }

  // Same reason as the intro: a bare `fitBounds` that cannot solve for the
  // canvas is a silent no-op, and a silent no-op here is a map that never
  // leaves the zoom it was parked at.
  const camera = solveCameraForBounds(map, [
    [bounds.minLon, bounds.minLat],
    [bounds.maxLon, bounds.maxLat],
  ]);
  map.jumpTo({
    center: camera?.center ?? [
      (bounds.minLon + bounds.maxLon) / 2,
      (bounds.minLat + bounds.maxLat) / 2,
    ],
    zoom: resolveFrameZoom({ zoom, solvedZoom: camera?.zoom, floorZoom }),
  });
}

/** The camera inputs, held so a resize can re-apply the frame it failed at. */
type CameraFrame = Omit<CameraInput, 'map'>;

type CameraCorners = [[number, number], [number, number]];
type SolvedCamera = ReturnType<MapLibreMapInstance['cameraForBounds']>;

/**
 * The fit, solved defensively.
 *
 * `cameraForBounds` has three answers, not two. It returns a camera; it returns
 * nothing and logs `Map cannot fit within canvas`; and — under the globe
 * projection the intro parks on, for a box it cannot solve — it dereferences
 * that nothing and throws. All three mean the same thing to the caller: there
 * is no computed frame, use the fallback. A throw that escapes here leaves the
 * map on the world zoom AND takes the render down with it.
 */
function solveCameraForBounds(
  map: MapLibreMapInstance,
  corners: CameraCorners,
): SolvedCamera | null {
  try {
    return (
      map.cameraForBounds(corners, {
        padding: MAP_FIT_PADDING,
        maxZoom: MAP_FIT_MAX_ZOOM,
      }) ?? null
    );
  } catch {
    return null;
  }
}

/**
 * Whether the canvas is big enough for a fit to be SOLVABLE, which is a
 * stricter question than whether it is big enough to draw. `fitBounds` subtracts
 * the padding from both axes and gives up when what is left is negative — so a
 * map that mounts into a box that has not been laid out yet asks for a frame
 * inside a canvas narrower than its own padding, gets nothing, and stays at
 * whatever zoom it was parked at. The retry hangs off this.
 */
function isContainerFittable(container: HTMLDivElement): boolean {
  const floor = MAP_FIT_PADDING * 2;
  return container.clientWidth > floor && container.clientHeight > floor;
}

interface MarkerRegistryEntry {
  marker: MapLibreMarkerInstance;
  point: MapCanvasPoint;
}

export function MapCanvas({
  dataElScope,
  points,
  zoom,
  center,
  isDark,
  accessibleName,
  focus = null,
  chosenKey = null,
  onMarkerPick,
  onUnavailable,
}: MapCanvasProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onUnavailableRef = useRef<() => void>(onUnavailable);
  const markersRef = useRef<MarkerRegistryEntry[]>([]);
  // The frame the camera was last asked for, and whether the canvas was big
  // enough to solve it. A map that mounts after first paint — inside a drill
  // pane, a rail card, a card that was not on screen — is asked to fit before
  // its box exists, and nothing ever asked again.
  const cameraFrameRef = useRef<CameraFrame | null>(null);
  const fittableRef = useRef(false);
  const shouldReduceMotion = useReducedMotion() === true;
  // Read from context rather than taken as a prop: threading it through
  // `Cmarkdownmap` and `MapSurface` would put the axis into two components that
  // have no use for it. A provider-less embedder gets the shipped pick.
  const ambientPoiVariant = useMapAmbientPoiVariant();
  const poiFollowUp = useMapPoiFollowUp();
  /* Null rather than a sentinel zoom: "no floor" and "a floor that happens to
     be low" are different instructions, and only one of them should survive a
     `Math.max`. */
  const floorZoom = followUpFloorsZoom(poiFollowUp) ? AMBIENT_POI_MIN_ZOOM : null;
  /** The floor the camera was last framed for, so a re-frame fires on a floor
   *  change and never on an unrelated dep moving. */
  const appliedFloorRef = useRef<number | null>(floorZoom);
  /** The one reusable ambient popup, held so picking an authored pin can close
   *  it — the two never speak at once. */
  const ambientPopupRef = useRef<MapLibrePopupInstance | null>(null);
  const [live, setLive] = useState<MapLibreLiveInstance | null>(null);
  // Armed at instance creation, consumed by the first camera pass that has
  // something to fly to — so the globe descent plays once per expanded open,
  // never on later marker/camera syncs.
  const introPendingRef = useRef(false);

  // Held in a ref so a parent re-render never tears the map down and
  // re-allocates a context just because the callback identity changed.
  useEffect(() => {
    onUnavailableRef.current = onUnavailable;
  }, [onUnavailable]);

  // Same protection for the pick reporter: markers are mounted by an effect,
  // and a fresh callback identity must never remount the whole marker set.
  const onMarkerPickRef = useRef<((key: string) => void) | undefined>(onMarkerPick);
  useEffect(() => {
    onMarkerPickRef.current = onMarkerPick;
  }, [onMarkerPick]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let isMounted = true;
    let isStarting = false;
    /** Set once the GPU has refused this container, so the resize channel does
     *  not re-enter `startMap` and allocate a second dead canvas per frame
     *  while the embedder is switching to its list. */
    let isRefused = false;
    let instance: MapLibreMapInstance | null = null;
    let canvasElement: HTMLCanvasElement | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let loadTimer: ReturnType<typeof setTimeout> | null = null;

    function reportUnavailable(): void {
      if (isMounted) {
        onUnavailableRef.current();
      }
    }

    function handleContextLost(): void {
      reportUnavailable();
    }

    async function startMap(node: HTMLDivElement): Promise<void> {
      if (isStarting || isRefused || instance || !isMounted) {
        return;
      }
      isStarting = true;

      try {
        await import('./map-canvas-styles');
        const maplibre = await import('maplibre-gl');

        // The awaits above are the window in which a fast collapse or unmount
        // can happen. Re-check everything before allocating the context.
        if (!isMounted || instance || !isContainerRenderable(node)) {
          return;
        }

        // Global library config; must be in force before the first map is
        // constructed, and is a no-op on every call after the first.
        ensureMapWorkerUrl(maplibre);

        let hasLoaded = false;
        const created = new maplibre.Map({
          container: node,
          style: mapStyleUrl(mapStyleForTheme(isDark)),
          // Chrome-free canvas (issue y29aiim): no maplibregl-ctrl-attrib.
          // The credit line lives in MAP_ATTRIBUTION_TEXT (map-style.ts).
          attributionControl: false,
          // Defaults to false, and with the default the repo's html-to-image
          // export path captures the map as a blank rectangle.
          canvasContextAttributes: { preserveDrawingBuffer: true },
          // A map inside a scrolling transcript must not swallow the page
          // scroll; ctrl/cmd + wheel (or two fingers) zooms instead.
          cooperativeGestures: true,
        });

        // A construction that stopped at the painter is not an instance this
        // component may publish: nothing below — the canvas handle, the
        // settle-resize, the markers, the camera, `remove()` — survives a map
        // with no painter, and each of those runs where React cannot contain
        // it. It is never assigned to `instance`, so the teardown below has
        // nothing to remove, and the block takes its own designed
        // unavailable path (the location list) instead.
        if (!isMapDrawable(created)) {
          isRefused = true;
          reportUnavailable();
          return;
        }

        instance = created;
        introPendingRef.current = true;

        // The globe perspective is part of the map's design, not a style
        // property: the style JSON is an external URL, so the projection is
        // set here, after the style parses and can no longer reset it.
        created.once(MAP_STYLE_LOAD_EVENT, () => {
          created.setProjection(MAP_GLOBE_PROJECTION);
        });

        created.once(MAP_LOAD_EVENT, () => {
          hasLoaded = true;
          if (loadTimer != null) {
            clearTimeout(loadTimer);
            loadTimer = null;
          }
        });

        // Not every failure announces itself. A worker that never starts emits
        // no map error and never reaches `load` — it just leaves a black
        // rectangle where the tiles should be. The deadline is what makes the
        // list fallback reachable in that case.
        loadTimer = setTimeout(() => {
          if (!hasLoaded) {
            reportUnavailable();
          }
        }, MAP_LOAD_TIMEOUT_MS);

        created.on(MAP_ERROR_EVENT, () => {
          // Once the style is up, a single failed tile is a gap in the picture,
          // not a dead map — tearing a working map down for it would be worse
          // than the gap. Before load, an error means the style or the tile
          // host never came up, and there is nothing to show.
          if (!hasLoaded) {
            reportUnavailable();
          }
        });

        canvasElement = created.getCanvas();
        canvasElement.setAttribute(ARIA_LABEL_ATTRIBUTE, accessibleName);
        canvasElement.addEventListener('webglcontextlost', handleContextLost);

        // The shell scales the canvas in; a transform changes no layout box, so
        // the observer below cannot see it settle.
        settleTimer = setTimeout(() => created.resize(), MAP_ENTER_SETTLE_MS);

        if (!isMounted) {
          return;
        }
        setLive({ maplibre, map: created });
      } catch {
        // WebGL unavailable, blocked, or the module failed to load.
        reportUnavailable();
      } finally {
        isStarting = false;
      }
    }

    // The observer is both the readiness trigger and the resize channel. React
    // commits the portal children before the portal host is attached to a slot,
    // so on expand this container is still detached when the effect first runs;
    // the observer fires as soon as it is attached and laid out.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            const node = containerRef.current;
            if (!isMounted || !node) {
              return;
            }

            if (!instance) {
              if (isContainerRenderable(node)) {
                void startMap(node);
              }
              return;
            }

            if (node.clientWidth > 0 && node.clientHeight > 0) {
              instance.resize();
            }

            // The fit is retried exactly once, on the edge where the canvas
            // becomes big enough to solve one. Without it a late-mounted map
            // keeps the world zoom it was parked at for as long as it is on
            // screen — twenty seconds later it is still a globe.
            const fittable = isContainerFittable(node);
            const wasFittable = fittableRef.current;
            fittableRef.current = fittable;
            const frame = cameraFrameRef.current;
            if (fittable && !wasFittable && frame) {
              applyCamera({ map: instance, ...frame });
            }
          });

    observer?.observe(container);

    if (isContainerRenderable(container)) {
      void startMap(container);
    }

    return () => {
      isMounted = false;
      observer?.disconnect();
      if (settleTimer != null) {
        clearTimeout(settleTimer);
      }
      if (loadTimer != null) {
        clearTimeout(loadTimer);
      }
      canvasElement?.removeEventListener('webglcontextlost', handleContextLost);
      // `Map.remove()` calls `this.painter.destroy()` and reads
      // `this.painter.context.gl` with no guard of its own, so removing a map
      // whose painter is gone — a context lost and never restored, since
      // `_contextRestored` re-runs `_setupPainter` and returns on the same
      // early exit — throws from inside a React cleanup. There is nothing to
      // release in that state anyway: the GL context is what `remove()` exists
      // to give back, and it is already gone.
      if (instance != null && isMapDrawable(instance)) {
        instance.remove();
      }
      instance = null;
      setLive(null);
    };
    // `isDark` is the only input that requires a different style URL, and so a
    // new instance. Points, camera and name are synced by the effects below so
    // the instance survives them — and survives the canvas <-> modal reparent.
  }, [isDark]);

  // Markers and camera are synced separately from creation so a later
  // geocoding pass can add positions without re-allocating the WebGL context.
  useEffect(() => {
    // Drawability is re-checked here rather than trusted from creation: a
    // context can be lost between the instance being published and this effect
    // running, and every call below (`Marker.addTo` → `Map.project`,
    // `applyCamera`, `jumpTo`) goes through the camera transform the painter
    // owns. A map that cannot be projected onto mounts no markers at all.
    if (!live || !isMapDrawable(live.map)) {
      return;
    }

    const markers = addMarkers({
      maplibre: live.maplibre,
      map: live.map,
      points,
      onPick: (key) => {
        onMarkerPickRef.current?.(key);
      },
    });
    markersRef.current = markers.map(({ marker }, markerIndex) => ({
      marker,
      point: points[markerIndex],
    }));
    // Recorded before the camera runs, so a resize that arrives after a fit
    // this canvas was too small to solve has the frame to re-apply.
    cameraFrameRef.current = { points, zoom, center, floorZoom };

    // The expanded-open intro: the camera waits over the pins at planet
    // altitude — a visible globe under the projection set at style.load —
    // and once the first tiles are in, descends onto the exact frame
    // `applyCamera` would have snapped to. Reduced motion keeps the snap.
    const introCenter =
      introPendingRef.current && !shouldReduceMotion
        ? introStartCenter({ points, center })
        : null;
    let introHoldTimer: ReturnType<typeof setTimeout> | null = null;
    let pendingLoadFlight: (() => void) | null = null;
    if (introCenter) {
      introPendingRef.current = false;
      live.map.jumpTo({ center: introCenter, zoom: MAP_INTRO_START_ZOOM });
      const beginIntroFlight = (): void => {
        pendingLoadFlight = null;
        introHoldTimer = setTimeout(() => {
          flyIntroCamera({ map: live.map, points, zoom, center, floorZoom });
        }, MAP_INTRO_HOLD_MS);
      };
      if (live.map.loaded()) {
        beginIntroFlight();
      } else {
        pendingLoadFlight = beginIntroFlight;
        live.map.once(MAP_LOAD_EVENT, beginIntroFlight);
      }
    } else {
      if (shouldReduceMotion) {
        introPendingRef.current = false;
      }
      applyCamera({ map: live.map, points, zoom, center, floorZoom });
    }

    return () => {
      // A dep change mid-intro (a geocoding pass landing new points) cancels
      // the stale flight; the re-run applies the fresh camera itself.
      if (introHoldTimer != null) {
        clearTimeout(introHoldTimer);
      }
      if (pendingLoadFlight) {
        live.map.off(MAP_LOAD_EVENT, pendingLoadFlight);
      }
      markersRef.current = [];
      markers.forEach(({ marker }) => {
        marker.remove();
      });
      // The marker buttons live in nested React roots, and this cleanup can
      // run while React is mid-render (a collapse, or a points re-sync
      // landing during a parent render). A root cannot be unmounted
      // synchronously from inside another render, so the unmounts wait a
      // tick; the marker DOM is already gone either way.
      setTimeout(() => {
        markers.forEach(({ reactRoot }) => {
          reactRoot.unmount();
        });
      }, 0);
    };
    // `floorZoom` is deliberately ABSENT from these deps. This effect owns the
    // markers as well as the camera, and re-running it removes and recreates
    // every marker's DOM and React root — so listing the floor here made a
    // presentation flip tear the pins down and drop the chosen-pin cue with
    // them. The floor gets its own re-frame below, which touches the camera and
    // nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, points, zoom, center, shouldReduceMotion]);

  // A floor change re-frames the camera, and only the camera.
  //
  // Guarded on the PREVIOUS floor rather than on the dep list, because the deps
  // have to name everything the frame is built from to stay honest, while the
  // only change this effect should act on is the floor itself. Without the
  // guard it would re-snap the camera on every points re-sync and undo a
  // reader's own pan.
  useEffect(() => {
    if (!live || appliedFloorRef.current === floorZoom) {
      return;
    }
    appliedFloorRef.current = floorZoom;
    applyCamera({ map: live.map, points, zoom, center, floorZoom });
  }, [live, floorZoom, points, zoom, center]);

  // Rail-card focus (tweak hdz7squ): fly to the pin. The chosen cue and the
  // popup ride `chosenKey` below — the flight is only the card path's extra,
  // because a pin picked ON the map is already under the reader's pointer.
  useEffect(() => {
    if (!live || !focus) {
      return;
    }

    const targetZoom = Math.max(live.map.getZoom(), MAP_FOCUS_MIN_ZOOM);
    if (shouldReduceMotion) {
      live.map.jumpTo({ center: [focus.lon, focus.lat], zoom: targetZoom });
    } else {
      live.map.flyTo({
        center: [focus.lon, focus.lat],
        zoom: targetZoom,
        duration: MAP_FOCUS_FLY_DURATION_MS,
        essential: true,
      });
    }
  }, [live, focus, shouldReduceMotion]);

  // The chosen pin, keyed by entry — from a rail-card click and a direct
  // marker pick alike. The chosen marker carries the cue class and holds its
  // named card; the rail card below it carries the details. The popup is NOT
  // opened here — it stays the explicit pin-click affordance (MapLibre's own
  // toggle), because opening it on every selection would duck the named card
  // the chosen treatment exists to show. What this does close is the popup a
  // PREVIOUS pick left open, so two pins never speak at once.
  useEffect(() => {
    if (!live) {
      return;
    }

    // An authored pick answers whatever the basemap was saying.
    if (chosenKey != null) {
      ambientPopupRef.current?.remove();
    }

    markersRef.current.forEach(({ marker, point }) => {
      const isChosen = chosenKey != null && point.key === chosenKey;
      marker.getElement().classList.toggle(MARKER_CHOSEN_CLASS, isChosen);
      const popup = marker.getPopup();
      if (!isChosen && popup && popup.isOpen()) {
        marker.togglePopup();
      }
    });
    // `points` is a dependency because a marker re-sync rebuilds the DOM: the
    // fresh markers arrive without the class, and the chosen state has to be
    // re-stamped onto them.
  }, [live, chosenKey, points]);

  useEffect(() => {
    if (!live) {
      return;
    }

    live.map.getCanvas().setAttribute(ARIA_LABEL_ATTRIBUTE, accessibleName);
  }, [live, accessibleName]);

  // Cartography is written into the live style, so it can only be applied once
  // the style has parsed — before `style.load` the layers it reads simply do
  // not exist. Both paths are taken unconditionally: `applyMapCartography` is
  // idempotent and returns on an empty layer set, so the immediate call is free
  // when the style is still in flight and is the ONLY cover for a style that
  // parsed before this effect attached. Deciding between them needs a
  // "has the style parsed" predicate, and the one that looks like it —
  // `isStyleLoaded()` — answers a different question and is false throughout
  // `style.load`; branching on it is what kept this treatment off the map.
  useEffect(() => {
    if (!live) {
      return;
    }

    const applyInk = (): void => {
      applyMapCartography(live.map);
    };

    applyInk();
    live.map.once(MAP_STYLE_LOAD_EVENT, applyInk);

    return () => {
      live.map.off(MAP_STYLE_LOAD_EVENT, applyInk);
    };
  }, [live]);

  // The basemap's own places, drawn as ambient context under the authored
  // pins. Same two-path shape as the cartography above and for the same
  // reason: the immediate call covers a style that parsed before this effect
  // attached, and the `style.load` hook covers one that has not parsed yet.
  // `applyMapAmbientPoi` removes its own layer first, so a variant change
  // re-runs this into one layer rather than stacking them.
  useEffect(() => {
    if (!live) {
      return;
    }

    // The block's own pin labels go with it, so the basemap's copy of a place
    // the block already authored stays out of the picture.
    const authoredNames = points.map((point) => point.label);
    const applyAmbient = (): void => {
      applyMapAmbientPoi(live.map, ambientPoiVariant, authoredNames);
    };

    applyAmbient();
    live.map.once(MAP_STYLE_LOAD_EVENT, applyAmbient);

    return () => {
      live.map.off(MAP_STYLE_LOAD_EVENT, applyAmbient);
    };
  }, [live, ambientPoiVariant, points]);

  // The authored pins' own footprint, claimed against the collision index.
  //
  // Pins are DOM markers, so the canvas has no knowledge of them and will place
  // an ambient name straight under one. This puts an invisible symbol at each
  // pin, appended ON TOP of the style: placement runs from the topmost symbol
  // layer down, so the reservation is placed first and every ambient label that
  // would have landed inside a pin is refused the space.
  useEffect(() => {
    if (!live) {
      return;
    }

    const applyReservation = (): void => {
      applyMapPinReservation({
        map: live.map,
        enabled: followUpReservesPins(poiFollowUp),
        points,
      });
    };

    applyReservation();
    live.map.once(MAP_STYLE_LOAD_EVENT, applyReservation);
    live.map.once(MAP_IDLE_EVENT, applyReservation);

    return () => {
      live.map.off(MAP_STYLE_LOAD_EVENT, applyReservation);
      live.map.off(MAP_IDLE_EVENT, applyReservation);
    };
  }, [live, poiFollowUp, points]);

  // Ambient POIs answer a click with their name and kind. The popup is a
  // single reusable instance: a second click moves it rather than leaving a
  // trail of open cards.
  useEffect(() => {
    if (!live) {
      return;
    }

    // `closeOnClick` is OFF and the dismissal is ours, because MapLibre's own
    // implementation is a `once('click')` armed when the popup opens — and on
    // the very click that moves this popup from one POI to the next, that
    // stale handler fires AFTER our layer handler has re-opened it and takes
    // the fresh card straight back down. The observed symptom was a POI that
    // needed two clicks to speak, with a blank frame in between.
    const popup = new live.maplibre.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: AMBIENT_POPUP_OFFSET,
    });
    ambientPopupRef.current = popup;

    const handleAmbientClick = (event: MapLibreLayerMouseEvent): void => {
      // A click that landed on an authored pin belongs to that pin, whatever
      // the basemap holds underneath it: MapLibre reports map clicks for
      // marker DOM too, and the pin's own popup is already opening.
      const target = event.originalEvent.target;
      if (
        target instanceof Element &&
        target.closest(MAPLIBRE_MARKER_SELECTOR) != null
      ) {
        return;
      }

      const feature = event.features?.[0];
      const text = feature ? ambientPoiText(feature.properties ?? {}) : null;
      if (!text) {
        return;
      }

      // Two places must never speak at once: an authored pin's popup closes as
      // the ambient one opens.
      markersRef.current.forEach(({ marker }) => {
        const markerPopup = marker.getPopup();
        if (markerPopup?.isOpen()) {
          marker.togglePopup();
        }
      });

      popup
        .setLngLat(event.lngLat)
        .setDOMContent(createAmbientPopupContent(text))
        .addTo(live.map);
    };

    // Dismissal, in place of the `closeOnClick` that had to come off: a click
    // anywhere that is NOT one of these POIs puts the card away.
    const handleMapClick = (event: MapLibreMapMouseEvent): void => {
      if (!live.map.getLayer(MAP_AMBIENT_POI_LAYER_ID)) {
        popup.remove();
        return;
      }
      const hits = live.map.queryRenderedFeatures(event.point, {
        layers: [MAP_AMBIENT_POI_LAYER_ID],
      });
      if (hits.length === 0) {
        popup.remove();
      }
    };

    const showPointer = (): void => {
      live.map.getCanvas().style.cursor = CURSOR_POINTER;
    };
    const clearPointer = (): void => {
      live.map.getCanvas().style.cursor = CURSOR_DEFAULT;
    };

    live.map.on(MAP_CLICK_EVENT, MAP_AMBIENT_POI_LAYER_ID, handleAmbientClick);
    live.map.on(MAP_CLICK_EVENT, handleMapClick);
    live.map.on(MAP_MOUSE_ENTER_EVENT, MAP_AMBIENT_POI_LAYER_ID, showPointer);
    live.map.on(MAP_MOUSE_LEAVE_EVENT, MAP_AMBIENT_POI_LAYER_ID, clearPointer);

    return () => {
      live.map.off(MAP_CLICK_EVENT, MAP_AMBIENT_POI_LAYER_ID, handleAmbientClick);
      live.map.off(MAP_CLICK_EVENT, handleMapClick);
      live.map.off(MAP_MOUSE_ENTER_EVENT, MAP_AMBIENT_POI_LAYER_ID, showPointer);
      live.map.off(MAP_MOUSE_LEAVE_EVENT, MAP_AMBIENT_POI_LAYER_ID, clearPointer);
      popup.remove();
      ambientPopupRef.current = null;
    };
  }, [live]);


  return (
      <div
        data-el={dataEl(dataElScope, 'MapCanvas/Root')}
        ref={containerRef}
        role="region"
        aria-label={accessibleName}
        style={MAP_CANVAS_ROOT_STYLE}
      />
  );
}
